import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, IconButton, Dialog } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import axiosInstance from 'utils/axiosInstance';
import { CameraSVG } from '@shared/cameraSVG';
import { APIResource } from 'constants/application';
import { getUrl } from 'utils/getURL';
import { PAN_STATUS_VARIANTS } from 'constants/panStatusVariants';
import { DocumentViewerModal } from '../modal/DocumentViewerModal';
import { UpdatePanModal } from '../modal/UpdatePanModal';
import { DocumentTipsModal } from '../modal/DocumentTipsModal';
import { LoaderStageStatus } from './LoaderScreen';
import { uploadDetailsStyles } from './UploadDetails.styles';
import { BasicDetailsOCR } from './BasicDetailsOCR';
import { GallerySVG } from '@shared/gallerySVG';
import { useTranslation } from "react-i18next";
import { getValidatePANFailure, IValidatePANResult, ValidatePAN } from 'store/slices/validatePAN';
import { resolveKycErrorMessage } from 'utils/kycErrorMessage';
import {
  fetchKycDocumentPresignedUrl,
  isRemoteDocumentDisplayUrl,
  isUsableDocumentDisplayUrl,
  resolveKycDocumentDisplayUrl,
} from 'utils/fetchKycDocumentPresignedUrl';
import { KYC_STATUS_VARIANTS } from 'constants/kycStatusVariants';
import { ErrorAlert } from '../../../../shared/ErrorAlert';
import { useEnhancedWebcam } from '@shared/fileUploaderWithCamera/useEnhancedWebcam';
import { WebcamCaptureOverlay } from '@shared/fileUploaderWithCamera/WebcamCaptureOverlay';

interface RootState {
  info: {
    allData: {
      customerId?: string;
      mobileNumber?: string;
      establishmentType?: string | null;
      kycStatus?: string;
      hasVerifiedActive?: boolean;
      panDetails?: { uploadedDocumentKey?: string } | null;
    };
  };
}

interface UploadDetailsProps {
  onVerificationComplete?: (payload: {
    imageSrc: string;
    panStatus: (typeof PAN_STATUS_VARIANTS)[keyof typeof PAN_STATUS_VARIANTS];
    panResponse: Record<string, unknown>;
    validationMessage?: string;
  }) => void;
  basicDetailsProps?: React.ComponentProps<typeof BasicDetailsOCR>;
}

interface PresignedUrlResponse {
  expiresAt: string;
  objectKey: string;
  presignedUrl: string;
  publicUrl: string;
}

interface PanOcrResponse {
  panNumber?: string;
  fullName?: string;
  establishmentType?: string;
  [key: string]: unknown;
}

type LoaderStage = {
  text: string;
  status: LoaderStageStatus;
};

const VERIFY_STAGE_TRANSITION_DELAY_MS = 1500;
const MIN_VERIFY_STAGE_VISIBLE_MS = 1500;
const SUCCESS_TICK_VISIBLE_MS = 1800;
const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'];
const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

const wait = (ms: number) => new Promise((resolve) => { setTimeout(resolve, ms); });

const isAllowedImageFile = (file: File) => {
  const mimeType = String(file.type || '').toLowerCase();
  const extension = (file.name.split('.').pop() || '').toLowerCase();
  if (ALLOWED_IMAGE_MIME_TYPES.includes(mimeType)) {
    return true;
  }
  return ALLOWED_IMAGE_EXTENSIONS.includes(extension);
};


export function UploadDetails({ onVerificationComplete, basicDetailsProps }: UploadDetailsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const resultSectionRef = useRef<HTMLDivElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const pendingGalleryVerifyRef = useRef(false);
  const info = useSelector((state: RootState) => state?.info?.allData);
  const [modalOpen, setModalOpen] = useState(false);
  const [tipsModalOpen, setTipsModalOpen] = useState(false);
  const [tipsAction, setTipsAction] = useState<'camera' | 'upload'>('camera');
  const [uploadedImageSrc, setUploadedImageSrc] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const lastObjectUrlRef = useRef<string | null>(null);
  const panImageRetryUsedRef = useRef(false);
  const [showLoader, setShowLoader] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cannotEditModalOpen, setCannotEditModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [loaderTitle, setLoaderTitle] = useState(t('kyc.panVerification', 'Verifying PAN'));
  const [loaderStages, setLoaderStages] = useState<LoaderStage[]>([
    { text: t('kyc.fetchingDetails', 'Fetching details'), status: 'pending' },
    { text: t('kyc.verifyingPan', 'Verifying PAN'), status: 'pending' },
  ]);
  const [verificationResult, setVerificationResult] = useState<{
    panStatus: (typeof PAN_STATUS_VARIANTS)[keyof typeof PAN_STATUS_VARIANTS];
    panResponse: Record<string, unknown>;
    validationMessage?: string;
  } | null>(null);

  const webcam = useEnhancedWebcam({
    onUsePhoto: (file, previewUrl) => {
      pendingGalleryVerifyRef.current = true;
      if (lastObjectUrlRef.current) {
        URL.revokeObjectURL(lastObjectUrlRef.current);
        lastObjectUrlRef.current = null;
      }
      setSelectedFile(file);
      setUploadedImageSrc(previewUrl);
      setVerificationResult(null);
      setModalOpen(false);
      basicDetailsProps?.onDelete?.();
    },
  });

  useEffect(() => {
    if (basicDetailsProps?.uploadedPanImage?.startsWith('blob:')) {
      lastObjectUrlRef.current = basicDetailsProps.uploadedPanImage;
    }
  }, [basicDetailsProps?.uploadedPanImage]);

  useEffect(() => {
    if (basicDetailsProps?.uploadedPanImage) {
      panImageRetryUsedRef.current = false;
      setUploadedImageSrc(basicDetailsProps.uploadedPanImage);
    }
  }, [basicDetailsProps?.uploadedPanImage]);

  // Resume path: resolve S3 object key via download-url when parent did not supply a display URL.
  useEffect(() => {
    const objectKey =
      (info?.panDetails as { uploadedDocumentKey?: string } | null | undefined)
        ?.uploadedDocumentKey || '';
    if (!objectKey || !info?.customerId) {
      return;
    }
    if (isUsableDocumentDisplayUrl(uploadedImageSrc)) {
      return;
    }
    if (isUsableDocumentDisplayUrl(basicDetailsProps?.uploadedPanImage)) {
      return;
    }

    let cancelled = false;
    (async () => {
      const displayUrl = await resolveKycDocumentDisplayUrl({
        customerId: info.customerId as string,
        documentType: 'PAN_CARD_PROOF',
        objectKey,
      });
      if (!cancelled && displayUrl) {
        setUploadedImageSrc(displayUrl);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    info?.customerId,
    info?.panDetails,
    uploadedImageSrc,
    basicDetailsProps?.uploadedPanImage,
  ]);

  const handlePanPreviewError = async () => {
    if (panImageRetryUsedRef.current) return;
    if (!isRemoteDocumentDisplayUrl(uploadedImageSrc)) return;
    const objectKey =
      (info?.panDetails as { uploadedDocumentKey?: string } | null | undefined)
        ?.uploadedDocumentKey ||
      (verificationResult?.panResponse as { uploadedDocumentKey?: string } | undefined)
        ?.uploadedDocumentKey ||
      '';
    if (!objectKey || !info?.customerId) return;
    panImageRetryUsedRef.current = true;
    const refreshed = await fetchKycDocumentPresignedUrl({
      customerId: info.customerId,
      documentType: 'PAN_CARD_PROOF',
      objectKey,
      force: true,
    }).catch(() => null);
    if (refreshed) {
      setUploadedImageSrc(refreshed);
    }
  };

  useEffect(() => {
    if (pendingGalleryVerifyRef.current && selectedFile && uploadedImageSrc) {
      pendingGalleryVerifyRef.current = false;
      handleVerifyWrapper();
    }
  }, [selectedFile, uploadedImageSrc]); // eslint-disable-line

  useEffect(() => {
    if (!verificationResult) {
      return;
    }

    const scrollToBottomTimeout = window.setTimeout(() => {
      resultSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });

      if (rootRef.current) {
        rootRef.current.scrollTo({
          top: rootRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 0);

    return () => {
      window.clearTimeout(scrollToBottomTimeout);
    };
  }, [verificationResult]);

  const updateStage = (index: number, status: LoaderStageStatus) => {
    setLoaderStages((prev) =>
      prev.map((stage, stageIndex) =>
        stageIndex === index ? { ...stage, status } : stage
      )
    );
  };

  const getSource = () => localStorage.getItem('appname') || 'BRP';

  const getPreferredFileName = (file: File) => {
    const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '').trim();
    const safeFileName = fileNameWithoutExtension
      .replace(/[^a-zA-Z0-9-_\s]/g, '')
      .replace(/\s+/g, '_');

    return safeFileName || `PAN_${Date.now()}`;
  };

  const getPanUploadUrl = async ({
    customerId,
    file,
  }: {
    customerId: string;
    file: File;
  }) => {
    const fileExtension = file.name.split('.').pop() || '';
    const fileName = getPreferredFileName(file);
    const response = await axiosInstance.post(
      getUrl(APIResource.uploadCancelledCheque,
        [
          {
            variableName: 'customerId',
            variableValue: customerId,
          },
        ]),
      {
        documentType: 'PAN_CARD_PROOF',
        fileExtension,
        fileName,
      },
      {
        params: { source: getSource() },
      }
    );

    return response.data as PresignedUrlResponse;
  };

  const uploadToS3 = async (uploadUrl: string, file: File) => {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
    });
  };

  const triggerPanOcr = async (objectKey: string) => {
    const response = await axiosInstance.post(
      getUrl(APIResource.panCardOcr, undefined, [
        {
          paramName: 'documentKey',
          paramValue: objectKey,
        },
      ])
    );

    return (response.data?.data || response.data) as PanOcrResponse;
  };

  const extractPanNumber = (ocrData: PanOcrResponse): string => {
    const panValue =
      (ocrData?.panNumber as string | undefined) ||
      (ocrData?.pan as string | undefined) ||
      (ocrData?.panNo as string | undefined) ||
      (ocrData?.pan_no as string | undefined) ||
      '';

    return String(panValue).trim().toUpperCase();
  };

  const extractEstablishmentType = (ocrData: PanOcrResponse): string => {
    return String(
      (ocrData?.establishmentType as string | undefined) ||
      ''
    ).trim();
  };

  const handleModalClose = () => {
    setModalOpen(false);
    // Discard the captured image — user chose not to verify
    if (lastObjectUrlRef.current) {
      URL.revokeObjectURL(lastObjectUrlRef.current);
      lastObjectUrlRef.current = null;
    }
    setUploadedImageSrc(null);
    setSelectedFile(null);
  };

  const openTipsForAction = (action: 'camera' | 'upload') => {
    setTipsAction(action);
    setTipsModalOpen(true);
  };

  const proceedOpenCamera = () => {
    pendingGalleryVerifyRef.current = false;
    webcam.handleOpenCamera();
  };

  const proceedOpenGallery = () => {
    pendingGalleryVerifyRef.current = true;
    galleryInputRef.current?.click();
  };

  const handleOpenCamera = () => {
    if (!uploadedImageSrc) {
      openTipsForAction('camera');
      return;
    }

    proceedOpenCamera();
  };

  const handleOpenGallery = () => {
    if (!uploadedImageSrc) {
      openTipsForAction('upload');
      return;
    }

    proceedOpenGallery();
  };

  const handleTipsGotIt = () => {
    setTipsModalOpen(false);
    if (tipsAction === 'camera') {
      proceedOpenCamera();
      return;
    }

    proceedOpenGallery();
  };

  const handleVerify = async () => {
    if (!uploadedImageSrc || !selectedFile) return;

    const customerId = info?.customerId;
    if (!customerId) {
      setError('Customer details are missing. Please login again.');
      return;
    }

    setModalOpen(false);
    setError('');
    setLoaderTitle(t('kyc.verifyingPan', 'Verifying PAN'));
    setLoaderStages([
      { text: t('kyc.fetchingDetails', 'Fetching details'), status: 'pending' },
      { text: t('kyc.verifyingPan', 'Verifying PAN'), status: 'pending' },
    ]);
    setShowLoader(true);

    const presignedUrlResponse = await getPanUploadUrl({
      customerId,
      file: selectedFile,
    });

    await uploadToS3(presignedUrlResponse.presignedUrl, selectedFile);

    const ocrData = await triggerPanOcr(presignedUrlResponse.objectKey);
    updateStage(0, 'success');

    const panNumber = extractPanNumber(ocrData);
    if (!panNumber) {
      throw new Error('Unable to read PAN number from the uploaded image.');
    }
    const establishmentTypeFromOcr = extractEstablishmentType(ocrData);

    await wait(VERIFY_STAGE_TRANSITION_DELAY_MS);

    const panValidationAction = await dispatch(
      ValidatePAN({
        data: {
          mobileNumber: info?.mobileNumber || '',
          customerId,
          panNumber,
          uploadedDocumentKey: presignedUrlResponse.objectKey,
          establishmentType: establishmentTypeFromOcr || info?.establishmentType || null,
          customerSource: getSource(),
        },
      }) as any
    );

    if (!ValidatePAN.fulfilled.match(panValidationAction)) {
      throw getValidatePANFailure(
        panValidationAction?.payload ||
        panValidationAction?.error?.message
      );
    }

    const panValidationResult = panValidationAction.payload as IValidatePANResult;
    const panStatus = panValidationResult?.panStatus || PAN_STATUS_VARIANTS.PENDING;
    const panResponse = (panValidationResult?.panResponse || {}) as Record<string, unknown>;
    const normalizedPanResponse = {
      ...panResponse,
      establishmentType:
        String((panResponse?.establishmentType as string | undefined) || '').trim() ||
        establishmentTypeFromOcr,
    };

    await wait(MIN_VERIFY_STAGE_VISIBLE_MS);

    if (panStatus !== PAN_STATUS_VARIANTS.PAN_VERIFIED) {
      const normalizedPanResponseRecord = normalizedPanResponse as Record<string, unknown>;
      const validationMessage =
        String((normalizedPanResponseRecord?.message as string | undefined) || '').trim() ||
        (panStatus === PAN_STATUS_VARIANTS.PAN_INACTIVE
          ? t('pan_not_compliant', 'PAN is not compliant.')
          : panStatus === PAN_STATUS_VARIANTS.PAN_INOPERATIVE
            ? t('pan_inoperative', 'PAN is inoperative.')
            : panStatus === PAN_STATUS_VARIANTS.PAN_ASSOCIATION
              ? t('pan_association', 'This PAN is linked to another customer ID.')
              : t('pan_verification_failed_retry', 'PAN verification failed. Please try again.'));

      const canProceed =
        panStatus === PAN_STATUS_VARIANTS.PAN_INACTIVE ||
        panStatus === PAN_STATUS_VARIANTS.PAN_INOPERATIVE;

      if (canProceed) {
        updateStage(1, 'success');
        await wait(SUCCESS_TICK_VISIBLE_MS);
        setShowLoader(false);
        setVerificationResult({
          panStatus,
          panResponse: normalizedPanResponse,
          validationMessage,
        });
        onVerificationComplete?.({
          imageSrc: uploadedImageSrc,
          panStatus,
          panResponse: normalizedPanResponse,
          validationMessage,
        });
      } else {
        handleVerificationError(validationMessage);
        setVerificationResult({
          panStatus,
          panResponse: normalizedPanResponse,
          validationMessage,
        });
        setTimeout(() => {
          setShowLoader(false);
          onVerificationComplete?.({
            imageSrc: uploadedImageSrc,
            panStatus,
            panResponse: normalizedPanResponse,
            validationMessage,
          });
        }, 1200);
      }
      return;
    }

    updateStage(1, 'success');

    await wait(SUCCESS_TICK_VISIBLE_MS);
    setShowLoader(false);
    setVerificationResult({
      panStatus,
      panResponse: normalizedPanResponse,
    });

    onVerificationComplete?.({
      imageSrc: uploadedImageSrc,
      panStatus,
      panResponse: normalizedPanResponse,
    });
  };

  const handleVerificationError = (message: string) => {
    setLoaderTitle(t('kyc.panVerificationFailed', 'PAN verification failed'));
    setError(message);

    setLoaderStages((prev) => {
      const firstPendingIndex = prev.findIndex((stage) => stage.status === 'pending');
      if (firstPendingIndex === -1) {
        return prev;
      }

      return prev.map((stage, index) =>
        index === firstPendingIndex ? { ...stage, status: 'error' } : stage
      );
    });
  };

  const handleVerifyWrapper = async () => {
    try {
      await handleVerify();
    } catch (err: unknown) {
      const { errorCode, message: fallbackMessage } = getValidatePANFailure(err);
      const message = errorCode
        ? resolveKycErrorMessage(errorCode, 'PAN')
        : fallbackMessage;

      handleVerificationError(message);

      setTimeout(() => {
        setShowLoader(false);
      }, 1200);
    }
  };


  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      return;
    }

    if (!isAllowedImageFile(selectedFile)) {
      pendingGalleryVerifyRef.current = false;
      setError(t('invalid_file_type'));
      event.target.value = '';
      return;
    }

    setError('');

    if (lastObjectUrlRef.current) {
      URL.revokeObjectURL(lastObjectUrlRef.current);
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    lastObjectUrlRef.current = objectUrl;
    setSelectedFile(selectedFile);
    setUploadedImageSrc(objectUrl);
    setVerificationResult(null);
    basicDetailsProps?.onDelete?.();

    if (pendingGalleryVerifyRef.current) {
      // useEffect will trigger verification once state updates land
    } else {
      setModalOpen(true);
    }

    event.target.value = '';
  };

  const handleEditClick = () => {
    setDeleteModalOpen(true);
  };

  const handleEditCancel = () => {
    setDeleteModalOpen(false);
  };

  const handleEditUploadPhoto = () => {
    setDeleteModalOpen(false);
    proceedOpenGallery();
  };

  const handleEditTakePhoto = () => {
    setDeleteModalOpen(false);
    proceedOpenCamera();
  };

  const isPersistedStatusForCurrentImage = Boolean(
    basicDetailsProps?.panStatus &&
    basicDetailsProps?.uploadedPanImage &&
    basicDetailsProps.uploadedPanImage === uploadedImageSrc
  );

  const shouldShowBasicDetails = Boolean(
    uploadedImageSrc && !showLoader && (verificationResult || isPersistedStatusForCurrentImage)
  );

  const activePendingIndex = loaderStages.findIndex((stage) => stage.status === 'pending');
  const inlineStatusMessage = (() => {
    if (activePendingIndex >= 0) {
      return loaderStages[activePendingIndex].text;
    }

    const errorStage = loaderStages.find((stage) => stage.status === 'error');
    if (errorStage) {
      return errorStage.text;
    }

    return loaderTitle;
  })();

  return (
    <Box ref={rootRef} className="upload-details-root" data-testid="upload-details-root" sx={uploadDetailsStyles.root}>
      <Box className="upload-details-title-section" data-testid="upload-details-title-section" sx={uploadDetailsStyles.titleSection}>
        <Typography className="upload-details-step-text" data-testid="upload-details-step-text" sx={uploadDetailsStyles.stepText}>
           {t("upload_pan_step_text")}
        </Typography>
        <Box className="upload-details-title-row" data-testid="upload-details-title-row" sx={uploadDetailsStyles.titleRow}>
          <Typography className="upload-details-title-text" data-testid="upload-details-title-text" sx={uploadDetailsStyles.titleText}>
            {t("upload_pan_card_title")}
          </Typography>
          <Typography className="upload-details-required-mark" data-testid="upload-details-required-mark" sx={uploadDetailsStyles.requiredMark}>
            *
          </Typography>
        </Box>
        <Typography className="upload-details-subtitle" data-testid="upload-details-subtitle" sx={uploadDetailsStyles.subtitle}>
          {t("upload_pan_card_subtitle")}
        </Typography>
      </Box>

      <Box className="upload-details-content" data-testid="upload-details-content" sx={uploadDetailsStyles.content}>
        {uploadedImageSrc ? (
          <Box
            className="upload-details-upload-area"
            data-testid="upload-details-upload-area"
            sx={{
              ...uploadDetailsStyles.uploadArea
            }}
          >
            <Box className="upload-details-uploaded-image-container" data-testid="upload-details-uploaded-image-container" sx={uploadDetailsStyles.uploadedImageContainer}>
                <Box
                  component="img"
                  className="upload-details-uploaded-pan-image"
                  data-testid="upload-details-uploaded-pan-image"
                  src={uploadedImageSrc}
                  alt="Uploaded PAN"
                  onError={handlePanPreviewError}
                  sx={uploadDetailsStyles.uploadedPanImage}
                />
                {info?.kycStatus === KYC_STATUS_VARIANTS.VERIFIED ||
                info?.kycStatus === KYC_STATUS_VARIANTS.VERIFIED_NOT_SUBMITTED ||
                info?.hasVerifiedActive ? (
                  <Box
                    className="upload-details-cannot-edit-button"
                    data-testid="upload-details-cannot-edit-button"
                    onClick={() => setCannotEditModalOpen(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setCannotEditModalOpen(true);
                      }
                    }}
                    sx={uploadDetailsStyles.editImageButton}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1.33331C4.31998 1.33331 1.33331 4.31998 1.33331 8C1.33331 11.68 4.31998 14.6666 8 14.6666C11.68 14.6666 14.6666 11.68 14.6666 8C14.6666 4.31998 11.68 1.33331 8 1.33331ZM8.66665 11.3333H7.33331V7.33331H8.66665V11.3333ZM8.66665 5.99998H7.33331V4.66665H8.66665V5.99998Z" fill="white"/>
                    </svg>
                    <Typography sx={uploadDetailsStyles.editButtonText}>
                      {t('cannot_edit', 'Cannot Edit')}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    className="upload-details-edit-image-button"
                    data-testid="upload-details-edit-image-button"
                    onClick={handleEditClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleEditClick();
                      }
                    }}
                    sx={uploadDetailsStyles.editImageButton}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M1.99915 11.5007V14.0007H4.49915L11.8725 6.62736L9.37248 4.12736L1.99915 11.5007ZM13.8058 4.69402C14.0658 4.43402 14.0658 4.01402 13.8058 3.75402L12.2458 2.19402C11.9858 1.93402 11.5658 1.93402 11.3058 2.19402L10.0858 3.41402L12.5858 5.91402L13.8058 4.69402Z" fill="white"/>
                    </svg>
                    <Typography sx={uploadDetailsStyles.editButtonText}>
                      {t('edit', 'Edit')}
                    </Typography>
                  </Box>
                )}
              </Box>
          </Box>
        ) : (
          <Box data-testid="upload-pan-action-row" className="upload-details-action-row" sx={{ ...uploadDetailsStyles.actionRow, height: '24vh' }} >
            <Paper
              className="upload-details-camera-action"
              data-testid="upload-details-camera-action"
              elevation={0}
              onClick={handleOpenCamera}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleOpenCamera();
                }
              }}
              sx={{ ...uploadDetailsStyles.actionItem, ...uploadDetailsStyles.cameraActionItem }}
            >
              <Box className="upload-details-camera-icon-circle" data-testid="upload-details-camera-icon-circle" sx={{...uploadDetailsStyles.actionIconCircle, ...uploadDetailsStyles.cameraActionIconCircle }}>
                <CameraSVG
                  className="upload-details-camera-icon"
                  data-testid="upload-details-camera-icon"
                  style={uploadDetailsStyles.cameraIcon as React.CSSProperties}
                />
              </Box>
              <Typography className="upload-details-camera-title" data-testid="upload-details-camera-title" sx={uploadDetailsStyles.actionTitle}>
                 {t("take_photo")}
              </Typography>
              <Typography className="upload-details-camera-subtitle" data-testid="upload-details-camera-subtitle" sx={uploadDetailsStyles.actionSubtitle}>
                {t("open_camera")}
              </Typography>
            </Paper>

            <Paper
              className="upload-details-gallery-action"
              data-testid="upload-details-gallery-action"
              elevation={0}
              onClick={handleOpenGallery}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleOpenGallery();
                }
              }}
              sx={{ ...uploadDetailsStyles.actionItem, ...uploadDetailsStyles.galleryActionItem }}
            >
              <Box className="upload-details-gallery-icon-circle" data-testid="upload-details-gallery-icon-circle" sx={{ ...uploadDetailsStyles.actionIconCircle, ...uploadDetailsStyles.galleryActionIconCircle }}>
                <GallerySVG className="upload-details-gallery-icon" data-testid="upload-details-gallery-icon" style={uploadDetailsStyles.galleryIcon as React.CSSProperties} />
              </Box>
              <Typography className="upload-details-gallery-title" data-testid="upload-details-gallery-title" sx={uploadDetailsStyles.actionTitle}>
                {t("upload_photo")}
              </Typography>
              <Typography className="upload-details-gallery-subtitle" data-testid="upload-details-gallery-subtitle" sx={uploadDetailsStyles.actionSubtitle}>
                {t("from_gallery")}
              </Typography>
            </Paper>
          </Box>
        )}

        {showLoader && (
          <Box className="upload-details-inline-loader-overlay" data-testid="upload-details-inline-loader-overlay" sx={uploadDetailsStyles.inlineLoaderOverlay}>
            <Box className="upload-details-inline-loader-icon" data-testid="upload-details-inline-loader-icon" sx={uploadDetailsStyles.inlineLoaderIconWrap}>
              <CircularProgress size={22} sx={{ color: '#16A34A' }} />
            </Box>
            <Typography className="upload-details-inline-loader-status" data-testid="upload-details-inline-loader-status" sx={uploadDetailsStyles.inlineLoaderStatusText}>
              {inlineStatusMessage}
            </Typography>
          </Box>
        )}

        <input className="upload-details-gallery-input" data-testid="upload-details-gallery-input" ref={galleryInputRef} type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" onChange={handleFileSelected} style={uploadDetailsStyles.hiddenFileInput} />

        {uploadedImageSrc && !showLoader && error && (
          <Box data-testid="upload-details-error-alert" sx={{ display: 'flex', alignItems: 'center', gap: '12px', mt: '12px' }}>
            <Box sx={{ mt: '2px', display: 'flex' }}>
              <ErrorAlert />
            </Box>
            <Typography sx={{ color: '#B71B1C', fontFamily: 'Noto Sans', fontSize: '12px', fontWeight: 400, lineHeight: 'normal' }}>
              {error}
            </Typography>
          </Box>
        )}

        {shouldShowBasicDetails && basicDetailsProps && (
          <Box ref={resultSectionRef} className="upload-details-result-section" data-testid="upload-details-result-section" sx={uploadDetailsStyles.resultSection}>
            <BasicDetailsOCR
              {...basicDetailsProps}
              uploadedPanImage={uploadedImageSrc}
              hideHeader
              embedded
              inlineFooter
            />
          </Box>
        )}

        {/* Spacer so content scrolls clear of the fixed info banner + button panel */}
        {!uploadedImageSrc && (
          <Box aria-hidden="true" sx={{ flex: 1, minHeight: '125px' }} />
        )}
      </Box>

      <WebcamCaptureOverlay webcam={webcam} />

      <DocumentTipsModal
        open={tipsModalOpen}
        documentType="pan"
        action={tipsAction}
        onClose={() => setTipsModalOpen(false)}
        onGotIt={handleTipsGotIt}
      />

      <DocumentViewerModal
        open={modalOpen}
        onClose={handleModalClose}
        imageSrc={uploadedImageSrc}
        onVerify={handleVerifyWrapper}
      />

      <UpdatePanModal
        open={deleteModalOpen}
        onClose={handleEditCancel}
        onUploadPhoto={handleEditUploadPhoto}
        onTakePhoto={handleEditTakePhoto}
      />

      <Dialog
        open={cannotEditModalOpen}
        onClose={() => setCannotEditModalOpen(false)}
        PaperProps={{
          sx: { margin: 0, position: 'fixed', bottom: 0, left: 0, right: 0, borderRadius: '16px 16px 0 0', maxWidth: '100%', width: '100%', },
        }}
      >
        <Box sx={{ bgcolor: 'white', display: 'flex', flexDirection: 'column', gap: 2, pb: 3, pt: 2, px: 2, borderRadius: '16px 16px 0 0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography sx={{ fontFamily: "'Noto Sans', sans-serif", fontWeight: 700, fontSize: 18, color: 'rgba(0, 0, 0, 0.87)' }}>
              {t('why_cannot_edit_pan_title', { defaultValue: "Why can't I edit PAN details?" })}
            </Typography>
            <Box
              onClick={() => setCannotEditModalOpen(false)}
              role="button"
              tabIndex={0}
              sx={{ cursor: 'pointer', p: 0.5, ml: 1, flexShrink: 0 }}
            >
              <svg fill="none" viewBox="0 0 14 14" width="14" height="14">
                <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="black" fillOpacity="0.56" />
              </svg>
            </Box>
          </Box>
          <Typography sx={{ fontFamily: "'Noto Sans', sans-serif", fontWeight: 400, fontSize: 16, color: 'rgba(0, 0, 0, 0.87)', lineHeight: 1.5 }}>
            {t('cannot_edit_pan_message', { defaultValue: 'PAN details cannot be changed once submitted. Please contact your field officer for further help.' })}
          </Typography>
        </Box>
      </Dialog>


    </Box>
  );
}
